import React, { useEffect, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Mapping = ({ selectedItem, onClose, isEmbedded = false }) => {
  const [map, setMap] = useState(null);
  const [geoJsonLayer, setGeoJsonLayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedItem) return;

    setLoading(true);
    setError(null);

    // Determine GeoJSON file based on IA name
    let iaName = selectedItem.name_of_irrigators || selectedItem.ia_name || '';
    console.log('Selected IA:', iaName);
    iaName = iaName.trim().toUpperCase(); // Only trim and uppercase
    let geoJsonFile = `/GeoJson/${iaName}.geojson`;
    console.log('Loading GeoJSON file:', geoJsonFile);

    // Initialize map
    const mapInstance = L.map('mapping-container').setView([9.3445, 123.5167], 10);
    // setMap(mapInstance); // Removed to prevent infinite update loop

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18
    }).addTo(mapInstance);

    // Try to fetch the file as is, then with underscores, then with no spaces
    function tryFetchGeoJson(files, idx = 0) {
      if (idx >= files.length) {
        // Fuzzy match fallback: list all files and pick the closest
        fetch('/GeoJson')
          .then(res => res.text())
          .then(html => {
            // Parse file names from directory listing (works in dev, not in prod build)
            const matches = Array.from(html.matchAll(/href="([^"]+\.geojson)"/g)).map(m => m[1]);
            if (!matches.length) {
              setError(`GeoJSON file not found for IA: ${iaName}`);
              setLoading(false);
              return;
            }
            // Normalize function: remove spaces, dashes, underscores, and uppercase
            const normalize = s => s.replace(/\s|_|-/g, '').replace(/\.geojson$/i, '').toUpperCase();
            const normIa = normalize(iaName);
            let best = null, bestScore = 0;
            matches.forEach(file => {
              const normFile = normalize(file);
              let score = 0;
              // Simple score: count matching chars in order
              for (let i = 0, j = 0; i < normIa.length && j < normFile.length;) {
                if (normIa[i] === normFile[j]) {
                  score++;
                  i++;
                }
                j++;
              }
              if (score > bestScore) {
                bestScore = score;
                best = file;
              }
            });
            if (best) {
              fetch(`/GeoJson/${best}`)
                .then(response => {
                  if (!response.ok) throw new Error('Not found');
                  return response.json();
                })
                .then(data => {
                  const geoJsonLayerInstance = L.geoJSON(data, {
                    style: {
                      color: '#4caf50',
                      weight: 2,
                      fillColor: '#4caf50',
                      fillOpacity: 0.3
                    },
                    onEachFeature: (feature, layer) => {
                      if (feature.properties) {
                        layer.bindPopup(`
                          <div style="font-family: Arial, sans-serif;">
                            <h3 style="margin: 0 0 10px 0; color: #2e7d32;">${feature.properties.ia || feature.properties.name || 'Location'}</h3>
                            <p style="margin: 5px 0;"><strong>Lot Number:</strong> ${feature.properties.lot_number || 'N/A'}</p>
                            <p style="margin: 5px 0;"><strong>Area:</strong> ${feature.properties.calculated || feature.properties.area || 'N/A'}</p>
                          </div>
                        `);
                      }
                    }
                  }).addTo(mapInstance);
                  setGeoJsonLayer(geoJsonLayerInstance);
                  mapInstance.fitBounds(geoJsonLayerInstance.getBounds());
                  // Compute centroid
                  let allCoords = [];
                  data.features.forEach(feature => {
                    if (feature.geometry.type === 'MultiPolygon') {
                      feature.geometry.coordinates.forEach(polygon => {
                        polygon.forEach(ring => {
                          allCoords = allCoords.concat(ring);
                        });
                      });
                    } else if (feature.geometry.type === 'Polygon') {
                      feature.geometry.coordinates.forEach(ring => {
                        allCoords = allCoords.concat(ring);
                      });
                    }
                  });
                  if (allCoords.length > 0) {
                    let sumLat = 0, sumLng = 0;
                    allCoords.forEach(([lng, lat]) => {
                      sumLat += lat;
                      sumLng += lng;
                    });
                    const centroid = [sumLat / allCoords.length, sumLng / allCoords.length];
                    L.marker(centroid)
                      .addTo(mapInstance)
                      .bindPopup(`<div style="font-family: Arial, sans-serif; min-width: 200px;"><h3 style="margin: 0 0 10px 0; color: #2e7d32;">${selectedItem.name_of_irrigators || 'IA'}</h3><p style="margin: 5px 0;"><strong>Municipality:</strong> ${selectedItem.municipality}</p></div>`);
                    mapInstance.setView(centroid, 15);
                  }
                  setLoading(false);
                })
                .catch(() => {
                  setError('GeoJSON file found but failed to load.');
                  setLoading(false);
                });
            } else {
              setError(`GeoJSON file not found for IA: ${iaName}`);
              setLoading(false);
            }
          })
          .catch(() => {
            setError(`GeoJSON file not found for IA: ${iaName}`);
            setLoading(false);
          });
        return;
      }
      fetch(files[idx])
        .then(response => {
          if (!response.ok) {
            throw new Error('Not found');
          }
          return response.json();
        })
        .then(data => {
          const geoJsonLayerInstance = L.geoJSON(data, {
            style: {
              color: '#4caf50',
              weight: 2,
              fillColor: '#4caf50',
              fillOpacity: 0.3
            },
            onEachFeature: (feature, layer) => {
              if (feature.properties) {
                layer.bindPopup(`
                  <div style="font-family: Arial, sans-serif;">
                    <h3 style="margin: 0 0 10px 0; color: #2e7d32;">${feature.properties.ia || feature.properties.name || 'Location'}</h3>
                    <p style="margin: 5px 0;"><strong>Lot Number:</strong> ${feature.properties.lot_number || 'N/A'}</p>
                    <p style="margin: 5px 0;"><strong>Area:</strong> ${feature.properties.calculated || feature.properties.area || 'N/A'}</p>
                  </div>
                `);
              }
            }
          }).addTo(mapInstance);
          setGeoJsonLayer(geoJsonLayerInstance);

          // Fit map to GeoJSON bounds
          mapInstance.fitBounds(geoJsonLayerInstance.getBounds());

          // Compute centroid of all polygons in the GeoJSON
          let allCoords = [];
          data.features.forEach(feature => {
            if (feature.geometry.type === 'MultiPolygon') {
              feature.geometry.coordinates.forEach(polygon => {
                polygon.forEach(ring => {
                  allCoords = allCoords.concat(ring);
                });
              });
            } else if (feature.geometry.type === 'Polygon') {
              feature.geometry.coordinates.forEach(ring => {
                allCoords = allCoords.concat(ring);
              });
            }
          });
          if (allCoords.length > 0) {
            // Calculate centroid
            let sumLat = 0, sumLng = 0;
            allCoords.forEach(([lng, lat]) => {
              sumLat += lat;
              sumLng += lng;
            });
            const centroid = [sumLat / allCoords.length, sumLng / allCoords.length];
            L.marker(centroid)
              .addTo(mapInstance)
              .bindPopup(`<div style="font-family: Arial, sans-serif; min-width: 200px;"><h3 style="margin: 0 0 10px 0; color: #2e7d32;">${selectedItem.name_of_irrigators || 'IA'}</h3><p style="margin: 5px 0;"><strong>Municipality:</strong> ${selectedItem.municipality}</p></div>`);
            mapInstance.setView(centroid, 15);
          }
          setLoading(false);
        })
        .catch(() => tryFetchGeoJson(files, idx + 1));
    }

    // Try different filename variations
    const fileVariants = [
      `/GeoJson/${iaName}.geojson`,
      `/GeoJson/${iaName.replace(/ /g, '_')}.geojson`,
      `/GeoJson/${iaName.replace(/ /g, '')}.geojson`,
    ];
    tryFetchGeoJson(fileVariants);

    return () => {
      if (mapInstance) {
        mapInstance.remove();
      }
    };
  }, [selectedItem]);

  // Function to get coordinates for municipalities
  const getMunicipalityCoordinates = (municipality) => {
    const coordinates = {
      'Pilar': [9.3445, 123.5167],
      'San Miguel': [9.7869, 123.7535],
      'Dagohoy': [9.9361, 123.7279],
      'Alicia': [9.5858, 123.7265],
      'Talibon': [10.1353, 124.0403],
      'Mahayag': [9.7869, 123.7535],
      'Malinao': [9.3445, 123.5167], // Default coordinates for Malinao
    };
    return coordinates[municipality] || coordinates['Malinao'];
  };

  return (
    <>
      {!isEmbedded && <ModalOverlay />}
      <ModalContent $isEmbedded={isEmbedded}>
        {!isEmbedded && (
          <Header>
            <Title>Location Map - {selectedItem?.name_of_irrigators || 'IA Profile'}</Title>
            <CloseButton onClick={onClose}>×</CloseButton>
          </Header>
        )}
        <MapContainer id="mapping-container" $isEmbedded={isEmbedded}>
          {loading && (
            <LoadingOverlay>
              <LoadingText>Loading map...</LoadingText>
            </LoadingOverlay>
          )}
          {error && (
            <ErrorOverlay>
              <ErrorText>{error}</ErrorText>
            </ErrorOverlay>
          )}
        </MapContainer>
        <InfoPanel $isEmbedded={isEmbedded}>
          <InfoTitle>IA Information</InfoTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Name:</InfoLabel>
              <InfoValue>{selectedItem?.name_of_irrigators || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>President:</InfoLabel>
              <InfoValue>{selectedItem?.name_of_iaPresident || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Municipality:</InfoLabel>
              <InfoValue>{selectedItem?.municipality || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Service Area (ha):</InfoLabel>
              <InfoValue>{selectedItem?.serviceArea || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>FUSA (ha):</InfoLabel>
              <InfoValue>{selectedItem?.fusa || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Status:</InfoLabel>
              <InfoValue>{selectedItem?.status || 'N/A'}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </InfoPanel>
      </ModalContent>
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  width: ${props => props.$isEmbedded ? '100%' : '90%'};
  max-width: ${props => props.$isEmbedded ? 'none' : '1200px'};
  height: ${props => props.$isEmbedded ? '100%' : '80vh'};
  background: white;
  border-radius: ${props => props.$isEmbedded ? '0' : '8px'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: ${props => props.$isEmbedded ? 'none' : '0 10px 30px rgba(0, 0, 0, 0.3)'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(45deg, #2e7d32, #4caf50);
  color: white;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const MapContainer = styled.div`
  flex: 1;
  width: 100%;
  min-height: ${props => props.$isEmbedded ? 'calc(100vh - 200px)' : '400px'};
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const LoadingText = styled.div`
  color: #2e7d32;
  font-size: 1.2rem;
  font-weight: 600;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 1rem;
  text-align: center;
  padding: 20px;
`;

const InfoPanel = styled.div`
  padding: ${props => props.$isEmbedded ? '15px' : '20px'};
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  max-height: ${props => props.$isEmbedded ? '150px' : '200px'};
  overflow-y: auto;
`;

const InfoTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #2e7d32;
  font-size: 1.2rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: #212529;
  font-size: 1rem;
  padding: 5px 10px;
  background: white;
  border-radius: 4px;
  border: 1px solid #dee2e6;
`;

export default Mapping; 